import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { IconComponent } from './ui/icon.component';
import { AnimateDirective } from '../directives/animate.directive';
import { FooterComponent } from './footer.component';
import { HeaderComponent } from './header.component';
import { BLOG_POSTS, BlogPost } from '../data/blog.data';

@Component({
  selector: 'app-blog-page',
  standalone: true,
  imports: [CommonModule, RouterModule, IconComponent, AnimateDirective, FooterComponent, HeaderComponent],
  template: `
    <app-header></app-header>

    <main class="min-h-screen bg-[#111111] pt-28 pb-20">

      <!-- Hero Section -->
      <div class="relative overflow-hidden border-b border-white/5 pb-16 mb-16">
        <div class="absolute inset-0 bg-gradient-to-b from-[#FF5500]/5 to-transparent pointer-events-none"></div>
        <div class="container mx-auto px-4 md:px-6 text-center relative z-10 pt-10 md:pt-14">
          <h1 class="text-4xl md:text-6xl font-bold text-white mb-5 tracking-tight leading-tight">
            Blog <span class="text-[#FF5500]">beetrack</span>
          </h1>
          <p class="text-gray-400 text-lg md:text-xl max-w-2xl mx-auto">
            Artigos técnicos, legislação e boas práticas para empresas que operam em ambientes frigorificados.
          </p>
        </div>
      </div>

      <!-- Filter Bar -->
      <div class="container mx-auto px-4 md:px-6 mb-10">
        <div appAnimate class="flex flex-wrap gap-3 justify-center">
          @for (cat of categories; track cat) {
            <button (click)="activeCategory = cat"
                    class="px-5 py-2.5 rounded-full text-sm font-bold transition-all border"
                    [class.bg-[#FF5500]]="activeCategory === cat"
                    [class.text-[#111111]]="activeCategory === cat"
                    [class.border-[#FF5500]]="activeCategory === cat"
                    [class.bg-transparent]="activeCategory !== cat"
                    [class.text-gray-400]="activeCategory !== cat"
                    [class.border-white/10]="activeCategory !== cat"
                    [class.hover:border-[#FF5500]/40]="activeCategory !== cat"
                    [class.hover:text-white]="activeCategory !== cat">
              {{ cat }}
            </button>
          }
        </div>
      </div>

      <!-- Articles Grid -->
      <div class="container mx-auto px-4 md:px-6">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          @for (post of filteredPosts(); track post.id; let i = $index) {
            <a [routerLink]="['/blog', post.slug]"
               appAnimate [delay]="i * 100"
               class="bg-[#1a1a1a] rounded-2xl overflow-hidden border border-white/5 hover:border-[#FF5500]/30 group cursor-pointer transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#FF5500]/5 flex flex-col">

              <!-- Image -->
              <div class="relative overflow-hidden aspect-video">
                <img [src]="post.image"
                     [alt]="post.title"
                     class="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                     loading="lazy"
                     width="400"
                     height="225">
              </div>

              <!-- Content -->
              <div class="p-6 flex flex-col flex-1">
                <div class="flex flex-wrap items-center gap-2 md:gap-3 text-gray-500 text-xs mb-3">
                  <span class="px-2 py-0.5 rounded border text-white font-bold"
                        [ngClass]="post.categoryColor">
                    {{ post.category }}
                  </span>
                  <span>{{ post.date }}</span>
                  <span class="w-1 h-1 rounded-full bg-gray-600"></span>
                  <span>{{ post.readTime }} de leitura</span>
                </div>

                <h2 class="text-white font-bold text-lg leading-snug mb-3 group-hover:text-[#FF5500] transition-colors line-clamp-3">
                  {{ post.title }}
                </h2>

                <p class="text-gray-400 text-sm leading-relaxed line-clamp-3 flex-1">
                  {{ post.excerpt }}
                </p>

                <div class="mt-5 flex items-center gap-2 text-[#FF5500] text-sm font-semibold group-hover:gap-3 transition-all">
                  <span>Leia o artigo</span>
                  <app-icon name="arrow-right" [size]="14"></app-icon>
                </div>
              </div>
            </a>
          }
        </div>
      </div>

    </main>

    <app-footer></app-footer>
  `,
  styles: [`
    .line-clamp-3 {
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  `]
})
export class BlogPageComponent {
  posts: BlogPost[] = BLOG_POSTS;
  activeCategory = 'Todos';

  get categories(): string[] {
    const cats = ['Todos', ...new Set(this.posts.map(p => p.category))];
    return cats;
  }

  filteredPosts(): BlogPost[] {
    if (this.activeCategory === 'Todos') return this.posts;
    return this.posts.filter(p => p.category === this.activeCategory);
  }
}
